import { useEffect, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Phase H.3 — Public deletion-status page.
 *
 * Meta auditors will paste the URL returned by /api/instagram/data-deletion
 * into a browser. They expect to see a human-readable HTML page that:
 *   - Confirms the deletion was received
 *   - Shows the confirmation code
 *   - Reflects real status (pending / completed) by polling the backend
 *
 * Hash-routed: /#deletion-status=<confirmation_code>
 *
 * Returning raw JSON or "404 not found" is the most common failure reason
 * during Meta audits — explicit, friendly, code-displayed HTML is required.
 */
export function DeletionStatusPage({ confirmationCode, onClose }) {
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer = null;

    async function check() {
      try {
        const r = await fetch(`${API}/api/deletion-status/${encodeURIComponent(confirmationCode)}`);
        if (!alive) return;
        const data = await r.json();
        setDetails(data);
        setStatus(data.status || "unknown");
        // Poll a few times while the wipe is still in-flight
        if (data.status === "pending" && pollCount < 8) {
          timer = setTimeout(() => setPollCount((n) => n + 1), 1500);
        }
      } catch (e) {
        if (!alive) return;
        setStatus("error");
      }
    }
    check();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [confirmationCode, pollCount]);

  const StatusBadge = ({ value }) => {
    const map = {
      completed:  { label: "Completed",        color: "#4ade80", bg: "rgba(74,222,128,0.12)" },
      pending:    { label: "Processing",       color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
      checking:   { label: "Looking it up…",   color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
      unknown:    { label: "Not found",        color: "#9ca3af", bg: "rgba(156,163,175,0.12)" },
      error:      { label: "Couldn't reach",   color: "#f87171", bg: "rgba(248,113,113,0.12)" },
    };
    const s = map[value] || map.unknown;
    return (
      <span style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: "uppercase",
      }}>{s.label}</span>
    );
  };

  return (
    <div
      data-testid="deletion-status-page"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0E0E12 0%, #1B0F2E 60%, #1A0E20 100%)",
        color: "#EFE6FF",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ maxWidth: 580, width: "100%" }}>
        <div style={{ fontSize: 56, color: "#EC4899", textAlign: "center", marginBottom: 16 }}>✦</div>
        <h1 style={{ fontSize: 28, fontWeight: 600, margin: "0 0 8px 0", textAlign: "center" }}>
          Data Deletion Request
        </h1>
        <p style={{ color: "#B9A8D8", textAlign: "center", marginBottom: 32, fontSize: 15, lineHeight: 1.6 }}>
          We received your request to delete the Aura data associated with your Instagram account.
        </p>

        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(192,132,252,0.25)",
          borderRadius: 16,
          padding: "20px 22px",
          marginBottom: 20,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ color: "#9D8DC4", fontSize: 13 }}>Status</span>
            <StatusBadge value={status} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ color: "#9D8DC4", fontSize: 13 }}>Confirmation code</span>
            <code data-testid="deletion-confirmation-code" style={{
              color: "#EFE6FF", fontSize: 12, fontFamily: "monospace",
              background: "rgba(0,0,0,0.3)", padding: "2px 8px", borderRadius: 6,
            }}>{confirmationCode}</code>
          </div>
          {details?.requested_at && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "#9D8DC4", fontSize: 13 }}>Requested</span>
              <span style={{ color: "#B9A8D8", fontSize: 13 }}>
                {new Date(details.requested_at).toLocaleString()}
              </span>
            </div>
          )}
          {details?.completed_at && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#9D8DC4", fontSize: 13 }}>Completed</span>
              <span style={{ color: "#B9A8D8", fontSize: 13 }}>
                {new Date(details.completed_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <p style={{ color: "#8779A8", fontSize: 12.5, lineHeight: 1.6, textAlign: "center" }}>
          {status === "completed" && "All Instagram-linked data has been removed from Aura's servers."}
          {status === "pending" && "Your request is being processed. This page will refresh automatically."}
          {status === "unknown" && "We couldn't find a record matching this code. If you arrived from a Meta link, please try again in a few moments."}
          {status === "error" && "We hit a snag looking up your request. Please refresh the page."}
        </p>

        <div style={{ textAlign: "center", marginTop: 32 }}>
          <button
            onClick={onClose}
            data-testid="deletion-status-back-btn"
            style={{
              background: "transparent",
              border: "1px solid rgba(192,132,252,0.35)",
              color: "#C084FC",
              padding: "10px 22px",
              borderRadius: 12,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Back to Aura
          </button>
        </div>
      </div>
    </div>
  );
}
